import React, { useState, useEffect } from 'react';
import { adminAPI } from '../services/api';

interface User {
  id: string;
  username: string;
  email: string | null;
  is_active: boolean;
  is_admin: boolean;
  created_at: string;
}

const AdminPage: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [usersLimit, setUsersLimit] = useState<number>(10);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // New user form
  const [showAddUser, setShowAddUser] = useState(false);
  const [newUsername, setNewUsername] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [addingUser, setAddingUser] = useState(false);

  // Reset password form
  const [resetPasswordUser, setResetPasswordUser] = useState<string | null>(null);
  const [newUserPassword, setNewUserPassword] = useState('');
  const [resettingPassword, setResettingPassword] = useState(false);

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      setLoading(true);
      const data = await adminAPI.listUsers();
      // Handle new response format: { users: [...], total: number, limit: number }
      if (data.users) {
        setUsers(data.users);
        setUsersLimit(data.limit || 10);
      } else {
        // Fallback for old format (just array)
        setUsers(data);
      }
      setError(null);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Не удалось загрузить пользователей');
    } finally {
      setLoading(false);
    }
  };

  const isLimitReached = users.length >= usersLimit;

  const handleAddUser = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newUsername || !newPassword) return;

    try {
      setAddingUser(true);
      await adminAPI.createUser(newUsername, newPassword, newEmail || undefined);
      setNewUsername('');
      setNewPassword('');
      setNewEmail('');
      setShowAddUser(false);
      await loadUsers();
    } catch (err: any) {
      alert(err.response?.data?.detail || 'Не удалось создать пользователя');
    } finally {
      setAddingUser(false);
    }
  };

  const handleDeleteUser = async (username: string) => {
    if (!window.confirm(`Вы уверены, что хотите удалить пользователя "${username}"?`)) {
      return;
    }

    try {
      await adminAPI.deleteUser(username);
      await loadUsers();
    } catch (err: any) {
      alert(err.response?.data?.detail || 'Не удалось удалить пользователя');
    }
  };

  const handleToggleActive = async (username: string) => {
    try {
      await adminAPI.toggleUserActive(username);
      await loadUsers();
    } catch (err: any) {
      alert(err.response?.data?.detail || 'Не удалось изменить статус пользователя');
    }
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!resetPasswordUser || !newUserPassword) return;

    try {
      setResettingPassword(true);
      await adminAPI.resetUserPassword(resetPasswordUser, newUserPassword);
      setResetPasswordUser(null);
      setNewUserPassword('');
      alert('Пароль успешно сброшен');
    } catch (err: any) {
      alert(err.response?.data?.detail || 'Не удалось сбросить пароль');
    } finally {
      setResettingPassword(false);
    }
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr.includes('Z') ? dateStr : dateStr + 'Z');
    return date.toLocaleString('ru-RU');
  };

  if (loading) {
    return <div style={{ padding: '20px' }}>Загрузка...</div>;
  }

  if (error) {
    return (
      <div style={{ padding: '20px', color: '#d32f2f' }}>
        Ошибка: {error}
      </div>
    );
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <div>
          <h2 style={{ margin: 0 }}>Управление пользователями</h2>
          <p style={{ margin: '5px 0 0 0', color: '#666', fontSize: '14px' }}>
            Пользователей: {users.length} / {usersLimit}
            {isLimitReached && (
              <span style={{ color: '#d32f2f', marginLeft: '10px' }}>
                (лимит достигнут)
              </span>
            )}
          </p>
        </div>
        <button
          onClick={() => setShowAddUser(!showAddUser)}
          disabled={isLimitReached && !showAddUser}
          style={{
            padding: '10px 20px',
            backgroundColor: isLimitReached && !showAddUser ? '#ccc' : '#1976d2',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: isLimitReached && !showAddUser ? 'not-allowed' : 'pointer'
          }}
          title={isLimitReached ? `Достигнут лимит пользователей (${usersLimit})` : ''}
        >
          {showAddUser ? 'Отмена' : 'Добавить пользователя'}
        </button>
      </div>

      {/* Add User Form */}
      {showAddUser && (
        <div style={{
          backgroundColor: 'white',
          padding: '20px',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          marginBottom: '20px'
        }}>
          <h3 style={{ marginTop: 0 }}>Новый пользователь</h3>
          <form onSubmit={handleAddUser}>
            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '5px' }}>Логин *</label>
              <input
                type="text"
                value={newUsername}
                onChange={(e) => setNewUsername(e.target.value)}
                required
                style={{
                  width: '100%',
                  padding: '10px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  boxSizing: 'border-box'
                }}
              />
            </div>
            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '5px' }}>Пароль *</label>
              <input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                style={{
                  width: '100%',
                  padding: '10px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  boxSizing: 'border-box'
                }}
              />
            </div>
            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '5px' }}>Email (необязательно)</label>
              <input
                type="email"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  boxSizing: 'border-box'
                }}
              />
            </div>
            <button
              type="submit"
              disabled={addingUser}
              style={{
                padding: '10px 20px',
                backgroundColor: '#4caf50',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: addingUser ? 'not-allowed' : 'pointer'
              }}
            >
              {addingUser ? 'Создание...' : 'Создать'}
            </button>
          </form>
        </div>
      )}

      {/* Reset Password Modal */}
      {resetPasswordUser && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: '30px',
            borderRadius: '8px',
            minWidth: '400px'
          }}>
            <h3 style={{ marginTop: 0 }}>Сброс пароля для "{resetPasswordUser}"</h3>
            <form onSubmit={handleResetPassword}>
              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '5px' }}>Новый пароль *</label>
                <input
                  type="password"
                  value={newUserPassword}
                  onChange={(e) => setNewUserPassword(e.target.value)}
                  required
                  autoFocus
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    boxSizing: 'border-box'
                  }}
                />
              </div>
              <div style={{ display: 'flex', gap: '10px' }}>
                <button
                  type="submit"
                  disabled={resettingPassword}
                  style={{
                    padding: '10px 20px',
                    backgroundColor: '#1976d2',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: resettingPassword ? 'not-allowed' : 'pointer'
                  }}
                >
                  {resettingPassword ? 'Сброс...' : 'Сбросить пароль'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setResetPasswordUser(null);
                    setNewUserPassword('');
                  }}
                  style={{
                    padding: '10px 20px',
                    backgroundColor: '#f5f5f5',
                    color: 'black',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer'
                  }}
                >
                  Отмена
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Users Table */}
      <div style={{
        backgroundColor: 'white',
        borderRadius: '8px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        overflow: 'hidden'
      }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ backgroundColor: '#f5f5f5' }}>
              <th style={{ padding: '15px', textAlign: 'left', borderBottom: '1px solid #ddd' }}>Логин</th>
              <th style={{ padding: '15px', textAlign: 'left', borderBottom: '1px solid #ddd' }}>Email</th>
              <th style={{ padding: '15px', textAlign: 'center', borderBottom: '1px solid #ddd' }}>Статус</th>
              <th style={{ padding: '15px', textAlign: 'center', borderBottom: '1px solid #ddd' }}>Роль</th>
              <th style={{ padding: '15px', textAlign: 'left', borderBottom: '1px solid #ddd' }}>Создан</th>
              <th style={{ padding: '15px', textAlign: 'center', borderBottom: '1px solid #ddd' }}>Действия</th>
            </tr>
          </thead>
          <tbody>
            {users.map((user) => (
              <tr key={user.id} style={{ borderBottom: '1px solid #eee' }}>
                <td style={{ padding: '15px' }}>
                  <strong>{user.username}</strong>
                </td>
                <td style={{ padding: '15px', color: '#666' }}>
                  {user.email || '-'}
                </td>
                <td style={{ padding: '15px', textAlign: 'center' }}>
                  <span style={{
                    padding: '4px 12px',
                    borderRadius: '20px',
                    backgroundColor: user.is_active ? '#e8f5e9' : '#ffebee',
                    color: user.is_active ? '#2e7d32' : '#c62828',
                    fontSize: '12px'
                  }}>
                    {user.is_active ? 'Активен' : 'Неактивен'}
                  </span>
                </td>
                <td style={{ padding: '15px', textAlign: 'center' }}>
                  <span style={{
                    padding: '4px 12px',
                    borderRadius: '20px',
                    backgroundColor: user.is_admin ? '#e3f2fd' : '#f5f5f5',
                    color: user.is_admin ? '#1565c0' : '#666',
                    fontSize: '12px'
                  }}>
                    {user.is_admin ? 'Админ' : 'Пользователь'}
                  </span>
                </td>
                <td style={{ padding: '15px', color: '#666', fontSize: '14px' }}>
                  {formatDate(user.created_at)}
                </td>
                <td style={{ padding: '15px', textAlign: 'center' }}>
                  <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                    <button
                      onClick={() => setResetPasswordUser(user.username)}
                      style={{
                        padding: '6px 12px',
                        backgroundColor: '#ff9800',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '12px'
                      }}
                    >
                      Сбросить пароль
                    </button>
                    {!user.is_admin && (
                      <>
                        <button
                          onClick={() => handleToggleActive(user.username)}
                          style={{
                            padding: '6px 12px',
                            backgroundColor: user.is_active ? '#9e9e9e' : '#4caf50',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '12px'
                          }}
                        >
                          {user.is_active ? 'Деактивировать' : 'Активировать'}
                        </button>
                        <button
                          onClick={() => handleDeleteUser(user.username)}
                          style={{
                            padding: '6px 12px',
                            backgroundColor: '#f44336',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '12px'
                          }}
                        >
                          Удалить
                        </button>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AdminPage;
